import React from "react";
import Chart from "react-apexcharts";
import Income from "../../../../assets/img/reports/Income.svg";
import Expenses from "../../../../assets/img/reports/Expenses.svg";
import Sales from "../../../../assets/img/reports/Sales.svg";
import Registrations from "../../../../assets/img/reports/Registrations.svg";
import { dashboardColors, theme } from "../../../../themes/Theme";
import { formatCurrency, formatNumber } from "../AccountLevelReportConstant";
import OverviewShimmer from "./OverviewShimmer";
import LoadingSpinner from "../../../../revampComponents/Atom/Loading";

function getSparkOptions(color) {
  return {
    chart: {
      type: "area",
      sparkline: { enabled: true },
      toolbar: { show: false },
    },
    stroke: { curve: "smooth", width: 1 },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.1,
        opacityFrom: 0.45,
        opacityTo: 0.05,
        stops: [0, 100],
      },
    },
    yaxis: {
      show: false,
      min: 0,  //  charts to start from 0
      forceNiceScale: true, 
    },
    colors: [color],
    tooltip: { enabled: false },
  };
}

const getChartData = (chartsData, field) => {
  if (!chartsData || chartsData.length === 0) {
    // Return fallback data if no chart data
    return Array(15).fill(0);
  }
  return chartsData?.map(item => item[field] || 0)
};

const MetricCard = ({ metricsData, selectedCurrency, loading = false }) => {
  const currencySymbol = selectedCurrency?.symbol || "₹";
  const isNonINR = selectedCurrency?.currency_name !== 'INR';
  if (loading) {
    return (
      <div className="metric-cards-container">
        {isNonINR ? <LoadingSpinner /> : <OverviewShimmer />}
      </div>
    );
  }



  const allStatCards = [
    {
      title: "Registrations",
      icon: Registrations,
      value: formatNumber(metricsData?.registrations || 0),
      color: "#8DA9F1",
      series: getChartData(metricsData?.chartsData, 'registrations_count'),
    },
    {
      title: "Sales",
      icon: Sales,
      value: formatCurrency(metricsData?.sales || 0, currencySymbol),
      color: "#FFDA8E",
      series: getChartData(metricsData?.chartsData, 'sale'),
    },
    {
      title: "Expenses",
      icon: Expenses,
      value: formatCurrency(metricsData?.expenses || 0, currencySymbol),
      color: "#93D7FD",
      series: getChartData(metricsData?.chartsData, 'expense'),
    },
    {
      title: "Income",
      value: formatCurrency(metricsData?.income || 0, currencySymbol),
      icon: Income,
      color: "#47DFCF",
      series: getChartData(metricsData?.chartsData, 'income'),
    },
  ];
  const statCards = isNonINR
    ? allStatCards.filter(card => ['Registrations', 'Sales'].includes(card.title))
    : allStatCards;

  return (
    <div className={`metric-cards-container ${isNonINR ? 'non-inr-layout' : 'inr-layout'}`}>
      {statCards?.map((card, i) => (
        <div key={card?.title} className="reports-overview-metric-card">
          <div className="d-flex align-items-center richtext-gap-4">
            <img
              className="report-metric-card-icon"
              src={card?.icon}
              alt={card?.title}
            />
            <div className="d-flex flex-column richtext-gap-1">
              <div className="report-metric-card-title">{card?.title}</div>
              <div className="report-metric-card-value">{card?.value}</div>
            </div>
          </div>

          <Chart
            options={getSparkOptions(card?.color)}
            series={[{ data: card?.series }]}
            type="area"
            width="100%"
            height={45}
          />
        </div>
      ))}

      <style>{`
        .reports-overview-metric-card {
          border-radius: 4px;
          padding: 12px 16px;
          background: #fff;
          border: 0.5px solid #C3CDDF
        }
        
        .report-metric-card-icon {
          width: 40px;
          height: 40px;
        }
        
        .metric-cards-container {
          display: grid;
          gap: 25px;
          width: 100%;
          grid-template-columns: 1fr;
        }
        .metric-cards-container.non-inr-layout {
          grid-template-columns: 1fr;
        }

        @media (min-width: 768px) {
          .metric-cards-container {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .metric-cards-container {
            grid-template-columns: repeat(4, 1fr);
          }
        }

        @media (min-width: 1200px) {
          .metric-cards-container {
            grid-template-columns: repeat(4, minmax(0, 300px));
            justify-content: center;
            gap: 25px;
          }
        }
        
        .report-metric-card-title {
          font-size: 16px;
          font-weight: 400;
          color: #333;
        }
        
        .report-metric-card-value {
          font-size: 24px;
          font-weight: 600;
          color: ${dashboardColors[theme].primaryTextColor};
        }

      `}</style>
    </div>
  );
};

export default MetricCard;
